import { useState, useRef, useEffect } from "react";
import type { ContentPiece } from "../../../../shared/types";
import { PLATFORM_LIMITS } from "./ContentCard";
import { Button } from "../ui/Button";
import { cn } from "../../lib/utils";

interface ContentEditorProps {
  piece: ContentPiece;
  onSave: (content: string) => void;
  onCancel: () => void;
}

export function ContentEditor({ piece, onSave, onCancel }: ContentEditorProps) {
  const [content, setContent] = useState(piece.content);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const limit = PLATFORM_LIMITS[piece.platform] || 5000;
  const charCount = content.length;
  const isOverLimit = charCount > limit;

  // Auto-resize textarea to fit content
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [content]);

  // Focus on mount
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleSave = () => {
    if (content.trim().length === 0) return;
    onSave(content);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
      {/* Platform label */}
      <div className="mb-3 text-sm font-semibold text-slate-700">
        Editing {piece.platform} content
      </div>

      {/* Textarea */}
      <textarea
        ref={textareaRef}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        className="w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm leading-relaxed text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200"
        rows={4}
      />

      {/* Character count */}
      <div className="mt-2 text-xs text-slate-500">
        <span
          className={cn(
            "font-medium",
            isOverLimit ? "text-red-600" : "text-slate-700",
          )}
        >
          {charCount}
        </span>
        /{limit}
        {isOverLimit && (
          <span className="ml-1 text-red-600">Over limit</span>
        )}
      </div>

      {/* Action buttons */}
      <div className="mt-4 flex gap-2">
        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          disabled={content.trim().length === 0 || content === piece.content}
        >
          Save
        </Button>
        <Button variant="secondary" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
